import {
    type Active,
    type UniqueIdentifier,
    type UseDraggableArguments,
    useDndMonitor,
    useDraggable,
    useDroppable,
} from '@dnd-kit/core';
import * as React from 'react';

import { useEventCallback, useLiveRef, useSafeLayoutEffect } from '@gitbook/util-react-hooks';

import type { DNDItem } from './types';

export interface DroppableState {
    isOver: boolean;
    setNodeRef: (element: HTMLElement | null) => void;
}

export interface DraggableState {
    isDragging: boolean;
    listeners: ReturnType<typeof useDraggable>['listeners'];
    setNodeRef: (element: HTMLElement | null) => void;
    setActivatorNodeRef: (element: HTMLElement | null) => void;
}

const activeDataRef = { current: null as { data: DNDItem; id: UniqueIdentifier } | null };

/**
 * Creates a draggable that persists data in a cache outside React.
 * `useDraggable` stores data in a React.ref, which is lost when the component is unmounted.
 * In some cases, with virtualization for example we could lose the original Component that holds `useDraggable`.
 * This hook prevents this by persisting the data in a cache outside React.
 */
export function usePersistentDraggable(args: UseDraggableArguments & { data: DNDItem }) {
    const drag = useDraggable(args);
    const dataRef = useLiveRef(args.data);
    useSafeLayoutEffect(() => {
        if (drag.isDragging) {
            activeDataRef.current = { id: args.id, data: dataRef.current };
        }
    }, [dataRef, args.id, drag.isDragging]);
    return drag;
}

/**
 * Retrieves the data of the active draggable.
 */
function getActiveData(active: Active) {
    // Try to use live data if available
    const liveData = active.data.current;
    if (liveData && 'id' in liveData) {
        return liveData as DNDItem;
    }

    // Try to use the data from the cache
    if (activeDataRef.current && activeDataRef.current?.id === active.id) {
        return activeDataRef.current.data;
    }

    // In this case we have a bug, we should never be in this state
    return {} as DNDItem;
}

/**
 * Wrapper around useDroppable that provides a more convenient API
 * where the callback can be local.
 *
 * For new implementations of drag and drop, we should probably not use this hook.
 */
export function useDroppableWithCallback(options: {
    disabled?: boolean;
    shouldAccept: (item: DNDItem) => boolean;
    onDrop: (item: DNDItem) => void;
    onHoverMove?: (event: {
        /** Item being dragged over */
        item: DNDItem;
        /** Current screen position of the draggable item */
        draggable: DOMRect;
        /** Current screen position of the drop element */
        over: DOMRect;
        /** Vertical position of the item in the drop element */
        yPosition: 'top' | 'bottom';
        /** Horizontal position of the item in the drop element */
        xPosition: 'left' | 'right';
    }) => void;
}): DroppableState {
    const { disabled, onDrop, onHoverMove } = options;
    const id = React.useId();
    const drop = useDroppable({ id, disabled });

    const shouldAccept = useEventCallback(options.shouldAccept);

    const accept = React.useMemo(() => {
        if (!drop.over || !drop.isOver || !drop.active) {
            return false;
        }

        const data = getActiveData(drop.active);
        return shouldAccept(data);
    }, [drop.over, drop.isOver, drop.active, shouldAccept]);

    useDndMonitor({
        onDragEnd: (event) => {
            if (accept && drop.active) {
                const data = getActiveData(event.active);
                onDrop(data);
            }
        },
        onDragMove: onHoverMove
            ? ({ active, over }) => {
                  if (!over || over.id !== id || over.disabled) {
                      return;
                  }

                  if (!active.rect.current || active.rect.current.translated === null) {
                      return;
                  }

                  const draggableRect = active.rect.current.translated;
                  const overRect = over.rect;

                  const pointer = {
                      x: draggableRect.left,
                      y: draggableRect.top,
                  };

                  const xPosition =
                      pointer.x > overRect.left + overRect.width / 2 ? 'right' : 'left';
                  const yPosition =
                      pointer.y > overRect.top + overRect.height / 2 ? 'bottom' : 'top';

                  onHoverMove({
                      item: active.data.current as DNDItem,
                      draggable: draggableRect as DOMRect,
                      over: overRect as DOMRect,
                      xPosition,
                      yPosition,
                  });
              }
            : undefined,
    });

    return {
        isOver: drop.isOver && accept,
        setNodeRef: drop.setNodeRef,
    };
}
